import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authStorage } from '../services/authStorage';
import { authApi, ApiError } from '../services/authApi';
import { User, Company } from '../types/auth';
import '../styles/CertificationStatusTable.css';
// @ts-ignore - jsPDF types
import jsPDF from 'jspdf';
// @ts-ignore - jspdf-autotable types
import 'jspdf-autotable';

type SortField = 'username' | 'email' | 'company' | 'userType' | 'isVerified' | 'isActive' | 'lastUpdatedDate';
type SortDirection = 'asc' | 'desc';

const UserReportTable: React.FC = () => {
    const { t } = useTranslation();
    const userRole = authStorage.getUserRole();
    const isAdmin = userRole === 'admin';
    const [data, setData] = useState<User[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [hideInactive, setHideInactive] = useState(false);
    const [hideInternal, setHideInternal] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
    const [sortField, setSortField] = useState<SortField>('username');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    useEffect(() => {
        // Debug logging for role detection
        console.log('[UserReportTable] User Role:', userRole, '| Is Admin:', isAdmin);
        const userData = authStorage.getUserData();
        console.log('[UserReportTable] User Data:', userData);
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError('');
            const token = authStorage.getAccessToken();
            if (!token) {
                setError('Authentication token not found. Please log in again.');
                return;
            }
            const [userResponse, companyResponse] = await Promise.all([
                authApi.getAllUsers(token),
                authApi.getAllCompanies(token)
            ]);
            setData(userResponse);
            setCompanies(companyResponse);
        } catch (err) {
            console.error('Failed to load user report:', err);
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('Failed to load user report. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (value: string | null | undefined) => {
        if (!value) return '-';
        try {
            const date = new Date(value);
            return date.toISOString().replace('T', ' ').substring(0, 16);
        } catch {
            return value;
        }
    };

    const getCompanyName = (user: User) => {
        if (user.company) return user.company.companyName;
        if (user.companyId) {
            const match = companies.find(c => c.id === user.companyId);
            if (match) return match.companyName;
            return `Company ${user.companyId}`;
        }
        return 'N/A';
    };

    const getUserTypeLabel = (userType: number) => {
        return userType === 1 ? 'Internal User' : 'Company User';
    };

    const sortedData = useMemo(() => {
        const copy = [...data];
        return copy.sort((a, b) => {
            let aVal: any;
            let bVal: any;

            switch (sortField) {
                case 'email':
                case 'username':
                    aVal = (a[sortField] || '').toLowerCase();
                    bVal = (b[sortField] || '').toLowerCase();
                    break;
                case 'company':
                    aVal = getCompanyName(a).toLowerCase();
                    bVal = getCompanyName(b).toLowerCase();
                    break;
                case 'userType':
                    aVal = getUserTypeLabel(a.userType).toLowerCase();
                    bVal = getUserTypeLabel(b.userType).toLowerCase();
                    break;
                case 'isVerified':
                case 'isActive':
                    aVal = a[sortField] ? 1 : 0;
                    bVal = b[sortField] ? 1 : 0;
                    break;
                case 'lastUpdatedDate':
                    aVal = a.lastUpdatedDate ? new Date(a.lastUpdatedDate).getTime() : 0;
                    bVal = b.lastUpdatedDate ? new Date(b.lastUpdatedDate).getTime() : 0;
                    break;
                default:
                    aVal = 0;
                    bVal = 0;
            }

            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [data, sortDirection, sortField]);

    const filteredData = useMemo(() => {
        return sortedData.filter(user => {
            const search = searchTerm.toLowerCase();
            const matches =
                user.username.toLowerCase().includes(search) ||
                user.email.toLowerCase().includes(search) ||
                getCompanyName(user).toLowerCase().includes(search);

            if (hideInactive && !user.isActive) return false;
            if (hideInternal && user.userType === 1) return false;
            if (selectedCompanyId !== null && user.companyId !== selectedCompanyId) return false;
            return matches;
        });
    }, [sortedData, searchTerm, hideInactive, hideInternal, selectedCompanyId]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, hideInactive, hideInternal, selectedCompanyId, sortField, sortDirection]);

    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredData.slice(start, start + pageSize);
    }, [filteredData, currentPage]);

    const toggleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) return '↕';
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    const exportToCsv = () => {
        if (!isAdmin) return;
        const headers = [
            'Username',
            'Email',
            'Company',
            'User Type',
            'Verified',
            'Active',
            'Last Updated'
        ];

        // Format date for CSV (shorter format to avoid Excel ########)
        const formatDateForCSV = (value: string | null | undefined) => {
            if (!value) return '-';
            try {
                const date = new Date(value);
                return date.toISOString().split('T')[0]; // YYYY-MM-DD format only
            } catch {
                return value;
            }
        };

        // Export preserves the same order currently shown on screen
        const rows = filteredData.map(user => [
            user.username,
            user.email,
            getCompanyName(user),
            getUserTypeLabel(user.userType),
            user.isVerified ? 'Yes' : 'No',
            user.isActive ? 'Yes' : 'No',
            formatDateForCSV(user.lastUpdatedDate)
        ]);

        const csv = [headers, ...rows]
            .map(line => line.map(cell => {
                if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
                    return '"' + cell.replace(/"/g, '""') + '"';
                }
                return cell;
            }).join(','))
            .join('\n');

        // Add UTF-8 BOM for proper Swedish character encoding
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `user_report_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadPDF = () => {
        console.log('[UserReportTable] Download PDF clicked. Is Admin:', isAdmin);
        if (!isAdmin) {
            console.warn('[UserReportTable] Download blocked: User is not admin');
            return;
        }

        try {
            // PDF preserves the same order currently shown on screen
            const generatedAt = new Date().toLocaleString();
            const dateStamp = new Date().toISOString().split('T')[0];

            // Create new PDF document
            const doc = new jsPDF();

            // Add title
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('User Mail Report', 14, 20);

            // Add generation date
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100);
            doc.text(`Generated: ${generatedAt}`, 14, 28);

            // Prepare table data
            const tableData = filteredData.map(user => [
                user.username,
                user.email,
                getCompanyName(user),
                getUserTypeLabel(user.userType),
                user.isVerified ? 'Yes' : 'No',
                user.isActive ? 'Yes' : 'No',
                formatDate(user.lastUpdatedDate)
            ]);

            // Add table using autoTable
            (doc as any).autoTable({
                startY: 35,
                head: [['Username', 'Email', 'Company', 'User Type', 'Verified', 'Active', 'Last Updated']],
                body: tableData,
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                },
                headStyles: {
                    fillColor: [245, 245, 245],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                },
                alternateRowStyles: {
                    fillColor: [250, 250, 250],
                },
                margin: { top: 35 },
            });

            // Save the PDF with clean filename
            doc.save(`User_Mail_Report_${dateStamp}.pdf`);

        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        }
    };

    if (loading) {
        return (
            <div className="certification-status-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading user report...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="certification-status-container">
                <div className="error-state">
                    <p className="error-message">{error}</p>
                    <button onClick={loadData} className="retry-button">Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="certification-status-container">
            <div className="certification-management-header">
                <h1>User Mail Report</h1>
            </div>

            <div className="controls-row">
                <div className="controls-left">
                    <div className="search-section">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search by user, email, or company..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                    </div>
                    <div className="filter-section">
                        <div className="filter-checkbox-group">
                            <label className="filter-checkbox">
                                <input
                                    type="checkbox"
                                    checked={hideInactive}
                                    onChange={(e) => setHideInactive(e.target.checked)}
                                />
                                <span>Hide inactive users</span>
                            </label>
                            <label className="filter-checkbox">
                                <input
                                    type="checkbox"
                                    checked={hideInternal}
                                    onChange={(e) => setHideInternal(e.target.checked)}
                                />
                                <span>Hide internal users</span>
                            </label>
                        </div>
                        <select
                            value={selectedCompanyId ?? ''}
                            onChange={(e) => setSelectedCompanyId(e.target.value ? Number(e.target.value) : null)}
                            className="filter-select"
                        >
                            <option value="">All companies</option>
                            {companies.map(company => (
                                <option key={company.id} value={company.id}>{company.companyName}</option>
                            ))}
                        </select>
                    </div>
                </div>
                {isAdmin && (
                    <div className="controls-right">
                        <button
                            onClick={downloadPDF}
                            className="print-report-button"
                            aria-label="Download User Mail Report as PDF"
                            data-testid="download-pdf-button"
                        >
                            📄 Download PDF
                        </button>
                        <button
                            onClick={exportToCsv}
                            className="export-csv-button"
                            aria-label="Export User Mail Report as CSV"
                            data-testid="export-csv-button"
                        >
                            📊 Export CSV
                        </button>
                    </div>
                )}
                {!isAdmin && (
                    <div className="controls-right" style={{ color: '#999', fontSize: '14px' }}>
                        <span title="Admin access required for print and export features">🔒 Admin Only</span>
                    </div>
                )}
            </div>

            <div className="results-summary">
                <p>Total users: {filteredData.length}</p>
            </div>

            <div className="certification-table-container">
                <table className="certification-table">
                    <thead>
                        <tr>
                            <th className="sortable" onClick={() => toggleSort('username')}>
                                Username {getSortIcon('username')}
                            </th>
                            <th className="sortable" onClick={() => toggleSort('email')}>
                                Email {getSortIcon('email')}
                            </th>
                            <th className="sortable" onClick={() => toggleSort('company')}>
                                Company {getSortIcon('company')}
                            </th>
                            <th className="sortable" onClick={() => toggleSort('userType')}>
                                User Type {getSortIcon('userType')}
                            </th>
                            <th className="sortable" onClick={() => toggleSort('isVerified')}>
                                Verified {getSortIcon('isVerified')}
                            </th>
                            <th className="sortable" onClick={() => toggleSort('isActive')}>
                                Active {getSortIcon('isActive')}
                            </th>
                            <th className="sortable" onClick={() => toggleSort('lastUpdatedDate')}>
                                Last Updated {getSortIcon('lastUpdatedDate')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length === 0 && (
                            <tr>
                                <td colSpan={7} className="no-data">
                                    {searchTerm ? 'No users found matching your search.' : 'No users available.'}
                                </td>
                            </tr>
                        )}
                        {paginatedData.map(user => (
                            <tr key={user.id} className="table-row">
                                <td className="company-name-cell">
                                    <div className="company-name-wrapper">
                                        <img
                                            src={`https://api.dicebear.com/7.x/shapes/svg?seed=${encodeURIComponent(user.username)}`}
                                            alt={user.username}
                                            className="company-avatar"
                                        />
                                        <span className="company-name">{user.username}</span>
                                    </div>
                                </td>
                                <td>{user.email}</td>
                                <td>{getCompanyName(user)}</td>
                                <td>{getUserTypeLabel(user.userType)}</td>
                                <td>{user.isVerified ? 'Yes' : 'No'}</td>
                                <td>{user.isActive ? 'Yes' : 'No'}</td>
                                <td className="date-cell">{formatDate(user.lastUpdatedDate)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredData.length > 0 && totalPages > 1 && (
                <div className="pagination">
                    <button
                        className="pagination-button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                    >
                        «
                    </button>
                    <button
                        className="pagination-button"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                    >
                        ‹
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                            key={page}
                            className={`pagination-button ${currentPage === page ? 'active' : ''}`}
                            onClick={() => setCurrentPage(page)}
                        >
                            {page}
                        </button>
                    ))}

                    <button
                        className="pagination-button"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                    >
                        ›
                    </button>
                    <button
                        className="pagination-button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                    >
                        »
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserReportTable;
